<template>
  <div class="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center px-4 py-12">
    <div class="max-w-md w-full">

      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Vérification de votre paiement...</p>
      </div>

      <!-- Success State -->
      <div v-else-if="success" class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>

        <h1 class="text-2xl font-bold text-gray-900 mb-2">Paiement Confirmé !</h1>
        <p class="text-gray-600 mb-6">Votre réservation a été confirmée avec succès.</p>

        <div v-if="appointmentDetails" class="bg-gray-50 rounded-xl p-4 mb-6 text-left">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Détails de votre rendez-vous</h3>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Salon</span>
              <span class="font-medium text-gray-900">{{ appointmentDetails.salonName }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Date</span>
              <span class="font-medium text-gray-900">{{ formatDate(appointmentDetails.date) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Montant payé</span>
              <span class="font-medium text-green-600">{{ appointmentDetails.amount }}€</span>
            </div>
          </div>
        </div>

        <p class="text-sm text-gray-500 mb-6">Un email de confirmation vous a été envoyé.</p>

        <div class="flex flex-col gap-3">
          <button
            @click="goToBookings"
            class="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            Voir mes rendez-vous
          </button>
          <button
            @click="goHome"
            class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>

        <h1 class="text-2xl font-bold text-gray-900 mb-2">Erreur de Vérification</h1>
        <p class="text-gray-600 mb-6">{{ errorMessage }}</p>

        <div class="flex flex-col gap-3">
          <button
            @click="retry"
            class="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            Réessayer
          </button>
          <button
            @click="goHome"
            class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const success = ref(false)
const errorMessage = ref('')
const appointmentDetails = ref(null)

const verifyPayment = async () => {
  try {
    loading.value = true
    const sessionId = route.query.session_id

    if (!sessionId) {
      throw new Error('Session ID manquant')
    }

    // Appel API pour vérifier le paiement
    const response = await axios.get(`https://bookmysalon-967a856b16b6.herokuapp.com/api/payments/verify-session/${sessionId}`)

    if (response.data.success) {
      success.value = true
      appointmentDetails.value = response.data.appointment || null
    } else {
      throw new Error(response.data.message || 'Échec de la vérification')
    }
  } catch (error) {
    console.error('Erreur lors de la vérification du paiement:', error)
    success.value = false
    errorMessage.value = error.response?.data?.message || error.message || 'Une erreur est survenue lors de la vérification du paiement'
  } finally {
    loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const goToBookings = () => {
  router.push('/mes-rendez-vous')
}

const goHome = () => {
  router.push('/')
}

const retry = () => {
  verifyPayment()
}

onMounted(() => {
  verifyPayment()
})
</script>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
